import instance from './axios-instance';
import { AppLog, Page } from '../../types';

export const VaultKeys = {
  VaultMatters: 'vaultMatters',
  VaultExports: 'vaultExports',
  VaultExportFiles: 'vaultExportFiles',
  MatterLogs: 'matterLogs',
};

export interface VaultExportFileModel {
  objectName: string;
  fileName: string;
  size: number;
  md5Hash: string;
}

export interface VaultMatterModel {
  matterId: string;
  name: string;
  description: string;
  state: string;
  matterRegion?: string;
}

export interface CloudStorageSinkModel {
  files: VaultExportFileModel[];
}

export interface ExportStatsModel {
  exportedArtifactCount?: number;
  totalArtifactCount?: number;
  sizeInBytes?: number;
}

export interface VaultExportModel {
  id: string;
  matterId: string;
  name: string;
  status: string;
  createTime: string;
  corpus?: string;
  dataScope?: string;
  searchMethod?: string;
  searchMethodTarget?: string;
  terms?: string;
  timeZone?: string;
  startTime?: string;
  endTime?: string;
  exportFormat?: string;
  cloudStorageSink?: CloudStorageSinkModel;
  exportStats?: ExportStatsModel;
  includeChatSpaces?: boolean;
  vaultExcludeDrafts?: boolean;
  methodTargetVersionDate?: string;
  peopleQuery?: string[];
  locationQuery?: string[];
  minusWords?: string[];
  responseStatuses?: string[];
}

export const getAllVaultMatters = async (): Promise<VaultMatterModel[]> => {
  const { data } = await instance.get<VaultMatterModel[]>(
    '/api/tools/vault-export'
  );

  return data;
};

export const createVaultMatter = async (
  model: Partial<VaultMatterModel>,
  changeLogMessage: string
): Promise<VaultMatterModel> => {
  const { data } = await instance.post('/api/tools/vault-export', model, {
    params: {
      changeLogMessage,
    },
  });
  return data;
};

export const updateVaultMatter = async (
  matterId: string,
  model: Partial<VaultMatterModel>,
  changeLogMessage: string
): Promise<VaultMatterModel> => {
  const { data } = await instance.put(
    `/api/tools/vault-export/${matterId}`,
    model,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return data;
};

export const deleteVaultMatter = async (
  matterIds: string[],
  changeLogMessage: string
): Promise<void> => {
  await instance.delete(`/api/tools/vault-export/delete`, {
    data: matterIds,
    params: {
      changeLogMessage,
    },
  });
};

export const getVaultExports = async (
  matterId: string,
  pageSize: number = 100,
  pageToken?: string
): Promise<Page<VaultExportModel[]>> => {
  const { data } = await instance.get<Page<VaultExportModel[]>>(
    `/api/tools/vault-export/${matterId}/exports`,
    {
      params: { pageSize, pageToken },
    }
  );

  return data;
};

export const getMatterAppLogs = async (matterId: string, cursor?: string) => {
  const { data } = await instance.get<Page<AppLog[]>>(
    `/api/tools/vault-export/${matterId}/logs`,
    {
      params: {
        cursor: cursor || undefined,
      },
    }
  );
  return data;
};

export const getAllVaultExports = async (
  matterId: string
): Promise<Page<VaultExportModel[]>> => {
  const { data } = await instance.get<Page<VaultExportModel[]>>(
    `/api/tools/vault-export/${matterId}/exports/all`
  );

  return data;
};

export const createVaultExport = async (
  matterId: string,
  model: Partial<VaultExportModel>,
  changeLogMessage: string
): Promise<VaultExportModel> => {
  const { data } = await instance.post(
    `/api/tools/vault-export/${matterId}/exports`,
    model,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return data;
};

export const getVaultExportFiles = async (
  matterId: string,
  exportId: string
): Promise<VaultExportFileModel[]> => {
  const { data } = await instance.get<VaultExportFileModel[]>(
    `/api/tools/vault-export/${matterId}/exports/${exportId}/files`
  );
  return data;
};

export const downloadVaultExportToDrive = async (
  matterId: string,
  exportId: string,
  payload: { filesToDownload: string[] },
  folderName: string,
  folderId: string,
  changeLogMessage: string
): Promise<void> => {
  await instance.post(
    `/api/tools/vault-export/${matterId}/exports/${exportId}/downloadVaultExportToDrive`,
    payload,
    {
      params: {
        folderName,
        folderId,
        changeLogMessage,
      },
    }
  );
};

export const deleteVaultExports = async (
  matterId: string,
  exportIds: string[],
  changeLogMessage: string
): Promise<void> => {
  await instance.delete(`/api/tools/vault-export/${matterId}/exports`, {
    data: exportIds,
    params: {
      changeLogMessage,
    },
  });
};

export const checkVaultAccess = async (): Promise<boolean> => {
  const { data } = await instance.get<boolean>(
    '/api/tools/vault-export/hasVaultAccess'
  );
  return data;
};
